import * as React from "react"
import { useCallback, useEffect, useState, useMemo } from "react"
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Button,
    Divider,
    Avatar,
    Chip,
    TextField,
    InputAdornment,
    CircularProgress,
    Grid
} from "@mui/material"
import { IconSeeker, ImageSeeker } from "@ui-seeker/core"
import { useAppToast } from "@ui-seeker/app"
import { postSubmit } from "@ui-seeker/utils"

import { BACKEND_APIS } from "@constants"
import { editMappingDrawer, tableData, imageComparisonModal, multipleRowsSelected, multipleRowsSent } from "../@signals"
import SkuOption from "./SkuOption"


export default function EditMappingDrawer() {
    const { showToast } = useAppToast()
    const drawerData = editMappingDrawer.value
    const multipleRows = multipleRowsSelected.value
    const isMultipleMode = multipleRows.length > 0
    const isOpen = drawerData !== null || isMultipleMode

    const [allSkuOptions, setAllSkuOptions] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [searchInput, setSearchInput] = useState('')
    const [selectedSkuId, setSelectedSkuId] = useState(null)
    const [visibleCount, setVisibleCount] = useState(30)

    const raw = drawerData?.raw
    const currentSku = drawerData?.sku
    const groupedProducts = drawerData?.groupedProducts
    const isGroupedMode = groupedProducts && groupedProducts.length > 1

    // For multiple mode, get brandId from first product (they should all be same brand for best UX)
    const brandId = isMultipleMode
        ? (multipleRows[0]?.raw?.id_brand || multipleRows[0]?.raw?.brand_id)
        : (raw?.id_brand || raw?.brand_id || raw?.brandId || raw?.brandID)

    // Filter SKUs locally based on search input
    // Supports AND search with comma-separated terms: "text1,text2" finds SKUs containing BOTH
    const filteredSkuOptions = useMemo(() => {
        if (!searchInput.trim()) return allSkuOptions

        // Split by comma and filter out empty terms
        const searchTerms = searchInput.toLowerCase().split(',').map(t => t.trim()).filter(Boolean)
        if (searchTerms.length === 0) return allSkuOptions

        // Helper function to check if SKU matches a single term
        const skuMatchesTerm = (sku, term) =>
            (sku.skuName && sku.skuName.toLowerCase().includes(term)) ||
            (sku.ean && sku.ean.toLowerCase().includes(term)) ||
            (sku.reference && sku.reference.toLowerCase().includes(term)) ||
            (sku.skuID && String(sku.skuID).includes(term))

        // SKU must match ALL terms (AND logic)
        return allSkuOptions.filter(sku =>
            searchTerms.every(term => skuMatchesTerm(sku, term))
        )
    }, [allSkuOptions, searchInput])

    // Paginated SKU options - only render visible items
    const visibleSkuOptions = useMemo(() => {
        return filteredSkuOptions.slice(0, visibleCount)
    }, [filteredSkuOptions, visibleCount])

    const hasMoreToShow = filteredSkuOptions.length > visibleCount

    // Load all SKUs once when drawer opens
    const loadSkus = useCallback(() => {
        if (!brandId) return

        setIsLoading(true)
        postSubmit(`${BACKEND_APIS.MATCHERS}/selectors/skus-by-brand/${brandId}`, {})
            .then(res => {
                setAllSkuOptions(res?.options || [])
            })
            .catch(() => {
                showToast.error('Error loading SKUs')
                setAllSkuOptions([])
            })
            .finally(() => setIsLoading(false))
    }, [brandId])

    // Load SKUs only once when drawer opens
    useEffect(() => {
        if (isOpen && brandId) {
            loadSkus()
        }
    }, [isOpen, brandId])

    // Reset state when drawer closes
    useEffect(() => {
        if (!isOpen) {
            setAllSkuOptions([])
            setSearchInput('')
            setSelectedSkuId(null)
            setVisibleCount(30)
        }
    }, [isOpen])

    // Reset visible count when search changes
    useEffect(() => {
        setVisibleCount(30)
    }, [searchInput])

    const handleClose = () => {
        editMappingDrawer.value = null
        multipleRowsSelected.value = []
    }

    const handleSelect = (skuId) => {
        setSelectedSkuId(skuId)
    }

    const handleImageClick = (sku) => {
        // For multiple mode, collect all raw images for collage
        const rawImages = isMultipleMode
            ? multipleRows.map(row => row.raw?.image).filter(Boolean)
            : [raw?.image].filter(Boolean)

        const rawTitle = isMultipleMode
            ? `${multipleRows.length} products selected`
            : raw?.title

        const rawVariant = isMultipleMode ? null : raw?.variant_raw

        imageComparisonModal.value = {
            title: rawTitle,
            variantRaw: rawVariant,
            rawImage: rawImages.length === 1 ? rawImages[0] : null,
            rawImages: rawImages.length > 1 ? rawImages : null,
            skuImage: `${BACKEND_APIS.BACKEND}/sku/image/${sku.skuID}`,
            skuName: sku.skuName
        }
    }

    const handleSave = async () => {
        if (isSaving || !selectedSkuId) return
        await handleSaveWithSkuId(selectedSkuId)
    }

    const handleSaveWithSkuId = async (skuId) => {
        if (isSaving || !skuId) return

        const selectedSkuData = allSkuOptions.find(s => s.skuID === skuId)
        const newSkuData = selectedSkuData ? {
            id_sku: selectedSkuData.skuID,
            name: selectedSkuData.skuName,
            brand: selectedSkuData.brand,
            image: `${BACKEND_APIS.BACKEND}/sku/image/${selectedSkuData.skuID}`
        } : null

        setIsSaving(true)
        try {
            if (isGroupedMode) {
                // Send N requests in parallel for grouped products
                const promises = groupedProducts.map(item =>
                    postSubmit(`/api/mapping/sku`, {
                        productID: item.raw?.product_id,
                        siteID: item.raw?.id_site,
                        skuID: skuId,
                        source: 'product-review-group'
                    })
                )
                await Promise.all(promises)

                const mappedKeys = groupedProducts.map(item => `${item.raw?.product_id}-${item.raw?.id_site}`)
                tableData.value = tableData.value.map(item => {
                    const key = `${item.raw?.product_id}-${item.raw?.id_site}`
                    if (mappedKeys.includes(key)) {
                        return { ...item, sku: newSkuData || item.sku }
                    }
                    return item
                })

                showToast.success(`${groupedProducts.length} mappings updated successfully`)
            } else {
                await postSubmit(`/api/mapping/sku`, {
                    productID: raw?.product_id,
                    siteID: raw?.id_site,
                    skuID: skuId,
                    source: 'product-review'
                })

                tableData.value = tableData.value.map(item => {
                    if (item.raw?.product_id === raw?.product_id && item.raw?.id_site === raw?.id_site) {
                        return { ...item, sku: newSkuData || item.sku }
                    }
                    return item
                })

                showToast.success('Mapping updated successfully')
            }

            handleClose()
        } catch (error) {
            showToast.error('Error updating mapping')
        } finally {
            setIsSaving(false)
        }
    }

    const handleRemoveMapping = async () => {
        setIsSaving(true)
        try {
            if (isGroupedMode) {
                const mappedProducts = groupedProducts.filter(item => item.sku)
                const promises = mappedProducts.map(item =>
                    postSubmit(`/api/mapping/sku`, {
                        productID: item.raw?.product_id,
                        siteID: item.raw?.id_site,
                        skuID: 0,
                        source: 'product-review-group'
                    })
                )
                await Promise.all(promises)

                const removedKeys = mappedProducts.map(item => `${item.raw?.product_id}-${item.raw?.id_site}`)
                tableData.value = tableData.value.map(item => {
                    const key = `${item.raw?.product_id}-${item.raw?.id_site}`
                    if (removedKeys.includes(key)) {
                        return { ...item, sku: null }
                    }
                    return item
                })

                showToast.success(`${mappedProducts.length} mappings removed`)
            } else {
                await postSubmit(`/api/mapping/sku`, {
                    productID: raw?.product_id,
                    siteID: raw?.id_site,
                    skuID: 0,
                    source: 'product-review'
                })

                tableData.value = tableData.value.map(item => {
                    if (item.raw?.product_id === raw?.product_id && item.raw?.id_site === raw?.id_site) {
                        return { ...item, sku: null }
                    }
                    return item
                })

                showToast.success('Mapping removed')
            }

            handleClose()
        } catch (error) {
            showToast.error('Error removing mapping')
        } finally {
            setIsSaving(false)
        }
    }

    const handleSaveMultiple = async () => {
        if (isSaving || !selectedSkuId || !isMultipleMode) return

        const selectedSkuData = allSkuOptions.find(s => s.skuID === selectedSkuId)

        setIsSaving(true)
        try {
            // Send all mappings in parallel
            const promises = multipleRows.map(row =>
                postSubmit(`/api/mapping/sku`, {
                    productID: row.raw?.product_id,
                    siteID: row.raw?.id_site,
                    skuID: selectedSkuId,
                    source: 'product-review-multiple'
                })
            )

            await Promise.all(promises)

            // Update local table data for all mapped products
            const mappedKeys = multipleRows.map(row => `${row.raw?.product_id}-${row.raw?.id_site}`)
            tableData.value = tableData.value.map(item => {
                const key = `${item.raw?.product_id}-${item.raw?.id_site}`
                if (mappedKeys.includes(key)) {
                    return {
                        ...item,
                        sku: selectedSkuData ? {
                            id_sku: selectedSkuData.skuID,
                            name: selectedSkuData.skuName,
                            brand: selectedSkuData.brand,
                            image: `${BACKEND_APIS.BACKEND}/sku/image/${selectedSkuData.skuID}`
                        } : item.sku
                    }
                }
                return item
            })

            // Signal to clear selection
            multipleRowsSent.value = mappedKeys

            showToast.success(`${multipleRows.length} mappings updated successfully`)
            handleClose()
        } catch (error) {
            showToast.error('Error updating mappings')
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <Drawer
            anchor="right"
            open={isOpen}
            onClose={handleClose}
            PaperProps={{
                sx: { width: { xs: '100%', sm: 600, md: 700 } }
            }}
        >
            {isOpen && (
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* Header */}
                    <Box sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid #e5e7eb'
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {isMultipleMode
                                ? `Edit Mapping (${multipleRows.length} products)`
                                : isGroupedMode
                                    ? `Edit Mapping (${groupedProducts.length} grouped)`
                                    : 'Edit Mapping'}
                        </Typography>
                        <IconButton onClick={handleClose}>
                            <IconSeeker icon="xmark" />
                        </IconButton>
                    </Box>

                    {/* Raw Product Info - Single Mode */}
                    {!isMultipleMode && (
                        <Box sx={{ p: 2, backgroundColor: '#fef3c7', borderBottom: '1px solid #e5e7eb' }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, color: '#92400e' }}>
                                Raw Product
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <ImageSeeker
                                    src={raw?.image}
                                    sx={{
                                        width: 100,
                                        height: 100,
                                        borderRadius: 1,
                                        border: '1px solid #e5e7eb',
                                        objectFit: 'contain',
                                        backgroundColor: '#fff'
                                    }}
                                />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        {raw?.title || '-'}
                                    </Typography>
                                    {raw?.subtitle && (
                                        <Typography variant="body2" color="text.secondary">
                                            {raw.subtitle}
                                        </Typography>
                                    )}
                                    {raw?.variant_raw && (
                                        <Chip
                                            label={raw.variant_raw}
                                            size="small"
                                            sx={{
                                                mt: 0.5,
                                                fontSize: '0.75rem',
                                                height: 22,
                                                fontWeight: 600,
                                                backgroundColor: '#e0e7ff',
                                                color: '#4338ca'
                                            }}
                                        />
                                    )}
                                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                            ID: {raw?.product_id}
                                        </Typography>
                                        {raw?.ean && (
                                            <Chip
                                                label={`EAN: ${raw.ean}`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '0.7rem', height: 20 }}
                                            />
                                        )}
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                        Site: {raw?.site} | Brand: {raw?.brand}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {/* Grouped Mode Indicator */}
                    {!isMultipleMode && isGroupedMode && (
                        <Box sx={{ px: 2, py: 1, backgroundColor: '#e0f2fe', borderBottom: '1px solid #bae6fd', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconSeeker icon="layer-group" sx={{ fontSize: 16, color: '#0369a1' }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#0369a1' }}>
                                This will map {groupedProducts.length} products with {raw?._groupField === 'product_url' ? 'product URL' : 'reference'} "{raw?._groupField === 'product_url' ? raw?.product_url : raw?.reference}"
                            </Typography>
                        </Box>
                    )}

                    {/* Multiple Products Info */}
                    {isMultipleMode && (
                        <Box sx={{ p: 2, backgroundColor: '#dbeafe', borderBottom: '1px solid #e5e7eb' }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, color: '#1e40af' }}>
                                Selected Products ({multipleRows.length})
                            </Typography>
                            <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
                                {multipleRows.map((row, idx) => (
                                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                                        <ImageSeeker
                                            src={row.raw?.image}
                                            sx={{ width: 40, height: 40, borderRadius: 0.5, objectFit: 'contain', backgroundColor: '#fff', flexShrink: 0 }}
                                        />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {row.raw?.title || '-'}
                                            </Typography>
                                            {(row.raw?.subtitle || row.raw?.variant_raw) && (
                                                <Typography variant="caption" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#6366f1' }}>
                                                    {[row.raw?.subtitle, row.raw?.variant_raw].filter(Boolean).join(' | ')}
                                                </Typography>
                                            )}
                                            <Typography variant="caption" color="text.secondary">
                                                {row.raw?.site} | {row.raw?.product_id}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Current Mapping - Only show in single mode */}
                    {!isMultipleMode && currentSku && (
                        <Box sx={{ p: 2, backgroundColor: '#d1fae5', borderBottom: '1px solid #e5e7eb' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ color: '#065f46' }}>
                                    Current Mapping
                                </Typography>
                                <Button
                                    size="small"
                                    color="error"
                                    startIcon={<IconSeeker icon="link-slash" />}
                                    onClick={handleRemoveMapping}
                                    disabled={isSaving}
                                >
                                    Remove
                                </Button>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <Avatar
                                    src={currentSku?.image}
                                    variant="rounded"
                                    sx={{ width: 50, height: 50, bgcolor: '#ecfdf5' }}
                                />
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>
                                        {currentSku?.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        SKU ID: {currentSku?.id_sku}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {/* Search - Now filters locally */}
                    <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb' }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Filter SKUs... (use comma for AND: text1,text2)"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconSeeker icon="search" sx={{ color: '#9ca3af' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchInput && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchInput('')}>
                                            <IconSeeker icon="xmark" sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            helperText={!isLoading && allSkuOptions.length > 0 ?
                                `Showing ${visibleSkuOptions.length} of ${filteredSkuOptions.length} SKUs${filteredSkuOptions.length !== allSkuOptions.length ? ` (${allSkuOptions.length} total)` : ''}` :
                                undefined
                            }
                        />
                    </Box>

                    {/* SKU List */}
                    <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                        {isLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : filteredSkuOptions.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 4, color: '#9ca3af' }}>
                                <IconSeeker icon="box-open" sx={{ fontSize: 48, mb: 1 }} />
                                <Typography>
                                    {searchInput ? 'No SKUs match your filter' : 'No SKUs found for this brand'}
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <Grid container spacing={2}>
                                    {visibleSkuOptions.map((sku) => (
                                        <Grid item xs={12} key={sku.skuID}>
                                            <SkuOption
                                                sku={sku}
                                                isSelected={selectedSkuId === sku.skuID}
                                                isSaving={isSaving}
                                                onSelect={() => handleSelect(sku.skuID)}
                                                onImageClick={() => handleImageClick(sku)}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                                {hasMoreToShow && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => setVisibleCount(prev => prev + 30)}
                                            startIcon={<IconSeeker icon="chevron-down" />}
                                        >
                                            Load more ({filteredSkuOptions.length - visibleCount} remaining)
                                        </Button>
                                    </Box>
                                )}
                            </>
                        )}
                    </Box>

                    {/* Footer */}
                    <Box sx={{
                        p: 2,
                        borderTop: '1px solid #e5e7eb',
                        display: 'flex',
                        gap: 2,
                        justifyContent: 'flex-end',
                        alignItems: 'center'
                    }}>
                        <Button
                            variant="outlined"
                            onClick={handleClose}
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={isMultipleMode ? handleSaveMultiple : handleSave}
                            disabled={isSaving || !selectedSkuId}
                            startIcon={isSaving ? <CircularProgress size={16} /> : <IconSeeker icon="check" />}
                        >
                            {isSaving ? 'Saving...' : isMultipleMode ? `Map ${multipleRows.length} Products` : isGroupedMode ? `Map ${groupedProducts.length} Products` : 'Accept'}
                        </Button>
                    </Box>
                </Box>
            )}
        </Drawer>
    )
}
